/**
 * The six things the studio can do to one invoice, on the row that names it.
 *
 * `.docs/24-ux-remediation.md` D5 asked for "issue, resend, retry now, record a manual
 * payment, refund". The dry run, Chase, Reconcile and the Wise queue landed in wave C;
 * these did not, and the two surfaces that print an invoice — the client detail's
 * *Invoices and payments* and the money page's ledger fold — rendered every one of
 * them with **no control at all**, including on a `sent` invoice nobody had paid and a
 * `processing` one. `invoices_issue$`, `invoices_charge$`, `invoices_resend`,
 * `payments_record_manual$` and `payments_refund!` are published, tested actions and
 * not one of them had a caller in the SPA.
 *
 * Session 44 adds the sixth, **Write off** — `invoices_write_off!`, money the studio
 * has decided not to collect, such as an invoice issued for more than the fee that
 * was agreed (`.docs/44-invoice-write-off.md`).
 *
 * ## Which moves a row gets, and where they come from
 *
 * `movesFor` reads the invoice's **status** and nothing else, because that is all the
 * money page's ledger row carries (`MoneyLedgerRowSchema` is six fields) and because
 * `invoiceMachine` is what decides. It is written as the states a row may still move
 * *from* — `CLAUDE.md` §Gotchas, "a guard over 'has this already been decided' is
 * written as the states a row may still move from" — so a status added tomorrow gets
 * the conservative answer rather than a button the API refuses.
 *
 * | status | moves |
 * |---|---|
 * | `draft` | Issue |
 * | `sent`, `failed` | Charge now · Send again · Record a payment · Write off |
 * | `processing` | Charge now · Send again · Record a payment |
 * | `paid` | Send again · Refund (where the row knows money arrived) |
 *
 * **Write off is `sent` and `failed` only**, because those are the two states
 * `billing/service.ts` `writeOff` accepts: a draft is not owed yet, a paid invoice has
 * nothing left on it, and `processing` is money in flight — decide once it has landed.
 * A `sent` invoice with a pending payment row is refused for the same reason, and the
 * row cannot see that, so the dialog shows the service's own sentence when it happens.
 *
 * **Refund is offered only where the row carries `paid_amount`.** The client detail's
 * table is `InvoiceView` and has it; the money page's ledger row is the compact view
 * and does not, so a Refund button there would be a guess at whether there is anything
 * to give back. `refundable` is that fact, passed by the surface that knows it.
 *
 * ## Who is offered them — a manager, and nobody else
 *
 * Every one of the six is closed to the restricted studio tier: five commit or forgive
 * money, `invoices_resend` names no `studioRole` and is closed by saying nothing, and
 * the `invoices_get` every dialog opens on is a manager's read too. Both surfaces that
 * render this cell are a manager's pages, which `guardStudioPortal` already redirects a
 * restricted account away from — and the cell asks `isManager` itself as well, the way
 * `pages/studio/taskDetail/ReviewPanel.tsx` does, so the day a surface the restricted
 * tier *can* reach prints an invoice it gets no buttons rather than six the registry
 * refuses. **Absent, not disabled**, which is the rule `shell/nav.ts` applies to every
 * nav item. Decided from the tier rather than from the catalogue's silence, for the
 * reason `ReviewPanel` gives: `_catalogue` is filtered per reader, and "this reader may
 * not" and "this build has no such tool" are different facts.
 *
 * ## Every amount comes from a re-read, not from the row
 *
 * The dialog opens on `invoices_get`, and it does that rather than trusting the row
 * for the reason a job re-reads a PaymentIntent (`CLAUDE.md` §Three doors): four of
 * these six actions state an amount the service then checks, and a stale total
 * would be a refusal the reader cannot explain. `invoices_issue$` is refused unless
 * `amount` equals the invoice total; `invoices_charge$` unless it equals what is
 * **outstanding**; `invoices_write_off!` when it is more than that;
 * `payments_record_manual$` takes whatever a person types. So the dialog reads the
 * invoice, and the confirm button states the figure it read.
 *
 * **What is outstanding is the invoice's own `outstanding`** — total − paid − written
 * off, `billing/queries.ts` `outstandingFrom`, worked out on the server on every read.
 * This file used to work it out as `total − paid_amount`, which a write-off makes
 * wrong: the world's Halcyon `FES-2026-003` spent three February days as $1,335.00
 * with $445.00 written off and nothing paid, and `total − paid` would have charged
 * $1,335.00 on an invoice that owed $890.00. The one thing done to it here is
 * `outstandingOf`'s floor at nothing — see there.
 *
 * ## What is not here
 *
 * **No cancel and no delete**, because there is no such action:
 * `ARCHITECTURE.md` §Deletion and `.docs/14` — an invoice is never edited. An issued
 * invoice that asks for more than is owed is settled for less with Write off, which
 * appends a row and edits nothing; any other correction is a `discount` line on the
 * next invoice, made with `invoices_draft$`. The refund dialog says which, because the
 * reader reaching for Refund is sometimes reaching for one of those instead.
 *
 * **`invoices_retry_charge$` is not one of the five.** It exists to re-run a
 * *numbered* attempt, and the number is what a rail's re-presentment limits are
 * counted against (`PAYMENTS.md`); picking one off a table is how somebody burns
 * attempt 3 by accident. `invoices_charge$` is "try it now" and lets the service count
 * the attempt, which is what D5's "retry now" means from a row. The numbered one
 * belongs beside the retry schedule on the failed-payments table, where
 * `FailedActions` already owns *Cancel retry*.
 */

import { useState } from 'react'

import {
  compare,
  isNegative,
  isZero,
  max,
  toMajorUnits,
  zero,
  type InvoiceDetailView,
  type Money,
  type PaymentView,
} from '@fareast/shared'
import { z } from 'zod'

import { messageOf } from '../../api/problem.js'
import { reviewOf, useStudioAction, useStudioRead } from '../../api/studio.js'
import { isManager, usePrincipal } from '../../auth/principal.js'
import { useFormat } from '../../format/useFormat.js'
import { Button, ButtonRow } from '../portal/Button.js'
import { Empty } from '../portal/Disclosure.js'
import { Input, Select, Textarea, useZodForm } from '../portal/Form.js'
import { Stack } from '../portal/Layout.js'
import { ConfirmDialog } from '../portal/Overlay.js'
import { amountOf } from '../portal/schemaForm.js'
import { Notice } from '../portal/Status.js'
import { B, Muted, Num, Prose, Ref, Small } from '../portal/Text.js'
import { WRITE_OFF_REASON } from './actionCopy.js'
import { NeedsReviewNotice } from './NeedsReviewNotice.js'

/** The six moves, by the action each one is. */
export type InvoiceMove = 'issue' | 'charge' | 'resend' | 'record' | 'write_off' | 'refund'

/** What each button says. A verb and its object, never "Manage". */
export const MOVE_LABEL: Readonly<Record<InvoiceMove, string>> = {
  issue: 'Issue',
  charge: 'Charge now',
  resend: 'Send again',
  record: 'Record a payment',
  write_off: 'Write off',
  refund: 'Refund',
}

/**
 * The states `invoices_write_off!` accepts — `billing/service.ts` `writeOff` refuses
 * a draft, a paid invoice and one with money in flight.
 *
 * One list, read by `movesFor` and by the refund dialog's sentence about which tool
 * corrects a wrong invoice, so the button and the sentence cannot disagree.
 */
const WRITE_OFF_FROM: readonly string[] = ['sent', 'failed']

/** True when an invoice in this state can still have part of it written off. */
export function writesOff(status: string): boolean {
  return WRITE_OFF_FROM.includes(status)
}

/**
 * Which moves an invoice in this state has.
 *
 * `refundable` is "money has arrived against it", which only a surface holding
 * `InvoiceView.paid_amount` can answer — see the header.
 */
export function movesFor(status: string, refundable: boolean): readonly InvoiceMove[] {
  if (status === 'draft') return ['issue']
  if (status === 'sent' || status === 'processing' || status === 'failed') {
    const unsettled: readonly InvoiceMove[] = writesOff(status)
      ? ['charge', 'resend', 'record', 'write_off']
      : ['charge', 'resend', 'record']
    return refundable ? [...unsettled, 'refund'] : unsettled
  }
  if (status === 'paid') return refundable ? ['resend', 'refund'] : ['resend']
  return []
}

export type InvoiceMovesProps = {
  /** The row this is the last cell of. Two fields, because the ledger row has six. */
  readonly invoice: { readonly ref: string; readonly status: string }
  /** True when money has arrived against it, where the surface knows. */
  readonly refundable?: boolean | undefined
}

export function InvoiceMoves({ invoice, refundable }: InvoiceMovesProps) {
  const [move, setMove] = useState<InvoiceMove | null>(null)
  const manager = isManager(usePrincipal())
  const moves = movesFor(invoice.status, refundable === true)

  /* A manager's six, and none of them for anybody else — see the header. */
  if (!manager || moves.length === 0) return null

  return (
    <>
      <ButtonRow>
        {moves.map((one) => (
          <Button
            key={one}
            size="sm"
            variant={
              one === 'issue'
                ? 'ochre'
                : one === 'refund' || one === 'write_off'
                  ? 'ghost'
                  : 'default'
            }
            aria-label={`${MOVE_LABEL[one]} — ${invoice.ref}`}
            onClick={() => {
              setMove(one)
            }}
          >
            {MOVE_LABEL[one]}
          </Button>
        ))}
      </ButtonRow>

      {move === null ? null : (
        <MoveDialog
          invoiceRef={invoice.ref}
          move={move}
          onClose={() => {
            setMove(null)
          }}
        />
      )}
    </>
  )
}

/* ------------------------------------------------------------------ the read */

/**
 * The invoice as it is **now**, then the move.
 *
 * A separate component so the read is not made until a button is pressed: eight rows
 * of a ledger would otherwise open eight `invoices_get` calls on every render.
 */
function MoveDialog({
  invoiceRef,
  move,
  onClose,
}: {
  readonly invoiceRef: string
  readonly move: InvoiceMove
  readonly onClose: () => void
}) {
  const read = useStudioRead('invoices_get', { invoice_ref: invoiceRef }, { inline: true })
  const invoice = read.data?.invoice

  if (invoice === undefined) {
    return (
      <ConfirmDialog
        open
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
        title={`${MOVE_LABEL[move]} — ${invoiceRef}`}
        confirmLabel={`Reading ${invoiceRef}…`}
        busy
        onConfirm={() => {
          /* Nothing to confirm until the invoice has been read. */
        }}
      >
        <Stack>
          {read.isPending ? (
            <Empty>Reading the invoice, its lines and every payment against it…</Empty>
          ) : (
            <Notice need role="alert">
              {messageOf(read.error)}
            </Notice>
          )}
        </Stack>
      </ConfirmDialog>
    )
  }

  if (move === 'issue') return <IssueDialog invoice={invoice} onClose={onClose} />
  if (move === 'charge') return <ChargeDialog invoice={invoice} onClose={onClose} />
  if (move === 'resend') return <ResendDialog invoice={invoice} onClose={onClose} />
  if (move === 'record') return <RecordDialog invoice={invoice} onClose={onClose} />
  if (move === 'write_off') return <WriteOffDialog invoice={invoice} onClose={onClose} />
  return <RefundDialog invoice={invoice} onClose={onClose} />
}

/**
 * What is still owed on it: the invoice's own `outstanding`, and never less than
 * nothing.
 *
 * The figure is the server's — total − paid − written off — and is read, not worked
 * out (see the header). The floor is the one thing this adds, and it is not
 * arithmetic on money so much as a refusal to state a sign: `outstanding` is below
 * zero on an invoice more arrived against than it asked for (Casaturca's world pays
 * three of them twice), and that is money to give back, which Refund is for — never
 * a figure a Charge, a Record or a Write off button may call owed.
 */
export function outstandingOf(invoice: InvoiceDetailView): Money {
  return max(invoice.outstanding, zero(invoice.outstanding.currency))
}

/* -------------------------------------------------------------------- issue */

function IssueDialog({
  invoice,
  onClose,
}: {
  readonly invoice: InvoiceDetailView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const issue = useStudioAction('invoices_issue')
  const review = reviewOf(issue.data)

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Issue an invoice"
      confirmLabel={`Issue ${format.money(invoice.total)} · ${invoice.ref}`}
      busy={issue.isPending}
      onConfirm={() => {
        issue.mutate(
          { invoice_ref: invoice.ref, amount: invoice.total },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`It moves to sent, takes today’s date and a due date seven days later, and the PDF is rendered and mailed to whoever on ${invoice.client} asked to hear about invoices. `}
          {/*
            Said before the click, not after it: an invoice is the one record in this
            system that cannot be taken back (`CLAUDE.md` §Records are appended), and a
            person reaching for Issue on the wrong row should read that here.

            Session 44: "a correction is a discount line on the next invoice" stopped being
            the whole answer. An issued invoice that asks for more than was agreed is now
            settled for less with Write off, on the invoice itself — so the sentence that
            told a person the client would owe all of it until next month says which is
            which.
          */}
          Nothing here can be edited or withdrawn afterwards. If it asks for more than was agreed,
          what it should not have asked for is written off on it; any other correction is a discount
          line on the next invoice.
        </Prose>
        <InvoiceLines invoice={invoice} />
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {issue.isError ? (
          <Notice need role="alert">
            {messageOf(issue.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* ------------------------------------------------------------------- charge */

function ChargeDialog({
  invoice,
  onClose,
}: {
  readonly invoice: InvoiceDetailView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const charge = useStudioAction('invoices_charge')
  const review = reviewOf(charge.data)
  const owed = outstandingOf(invoice)
  const nothing = isZero(owed)

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Charge an invoice now"
      confirmLabel={`Charge ${format.money(owed)} · ${invoice.ref}`}
      busy={charge.isPending || nothing}
      onConfirm={() => {
        if (nothing) return
        charge.mutate(
          { invoice_ref: invoice.ref, amount: owed },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`It takes ${format.money(owed)} off the payment method ${invoice.client} has on file, without waiting for the schedule. `}
          {/*
            `PAYMENTS.md` §Operations: "Bank debits settle slowly and report failures by
            webhook days later." A person who has just pressed this and sees
            `processing` should already know that is the answer rather than a stall.
          */}
          A card answers in a moment; a bank debit answers &ldquo;processing&rdquo; and the real
          outcome arrives days later by webhook. An account with no method on file, or one still in
          microdeposit verification, is answered and not charged.
        </Prose>
        {nothing ? (
          <Notice need role="alert">
            Nothing is outstanding on this invoice — what has been paid and written off against it
            already covers the total. There is nothing to charge.
          </Notice>
        ) : null}
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {charge.isError ? (
          <Notice need role="alert">
            {messageOf(charge.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* ------------------------------------------------------------------- resend */

function ResendDialog({
  invoice,
  onClose,
}: {
  readonly invoice: InvoiceDetailView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const resend = useStudioAction('invoices_resend')
  const review = reviewOf(resend.data)

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Send an invoice again"
      confirmLabel={`Send ${invoice.ref} again`}
      busy={resend.isPending}
      onConfirm={() => {
        resend.mutate(
          { invoice_ref: invoice.ref },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`The same mail goes out again to whoever on ${invoice.client} hears about invoices, with the PDF attached. `}
          Nothing about the invoice changes: no new reference, no new due date, no charge. It is
          still {format.money(invoice.total)}
          {invoice.due_on === null ? '' : ', due on the day it was already due'}.
        </Prose>
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {resend.isError ? (
          <Notice need role="alert">
            {messageOf(resend.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* --------------------------------------------------------- a payment by hand */

const RECORD_NOTE_MIN = 3

const RecordFormSchema = z.object({
  amount: z.string(),
  note: z.string(),
})

type RecordForm = z.infer<typeof RecordFormSchema>

function RecordDialog({
  invoice,
  onClose,
}: {
  readonly invoice: InvoiceDetailView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const record = useStudioAction('payments_record_manual')
  const review = reviewOf(record.data)
  const owed = outstandingOf(invoice)

  /*
   * The box opens on **what is outstanding**, not on the total.
   *
   * A partly-settled invoice is the case this exists for — a transfer that arrived
   * short, an on-account credit already placed — and seeding the total would have a
   * person confirm a figure larger than the one they are recording. It is a default
   * in an editable box rather than a fact this page states: the amount that commits is
   * whatever is typed, and the confirm button reads it back.
   */
  const form = useZodForm<RecordForm, RecordForm>(RecordFormSchema, {
    defaultValues: { amount: String(toMajorUnits(owed)), note: '' },
  })
  const typed = form.watch()
  const amount = amountOf(typed.amount, invoice.currency)
  const note = typed.note.trim()
  const refused = isZero(amount) || note.length < RECORD_NOTE_MIN

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Record a payment that arrived by hand"
      confirmLabel={`Record ${format.money(amount)} against ${invoice.ref}`}
      busy={record.isPending || refused}
      onConfirm={() => {
        if (refused) return
        record.mutate(
          { invoice_ref: invoice.ref, amount, note, source: 'manual' },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`Money that came in outside the gateways — a cheque, a transfer from an account we do not watch. `}
          {/*
            The three figures the server's `outstanding` is made of, read off the
            invoice rather than subtracted here. Over Halcyon's `FES-2026-003` between
            its write-off and its payment this said "$1,335.00 with $0.00 settled, so
            $1,335.00 is outstanding" — of an invoice that owed $890.00.
          */}
          {invoice.ref} is {format.money(invoice.total)} with {format.money(invoice.paid_amount)}{' '}
          settled
          {isZero(invoice.written_off)
            ? ''
            : ` and ${format.money(invoice.written_off)} written off`}
          , so {format.money(owed)} is outstanding. Recording less than that leaves it open;
          recording the rest settles it.
        </Prose>
        <Prose>
          <Small>
            <Muted>
              A Wise credit is not this. One that arrived and could not be placed is in the review
              queue on the money page, and matching it there is what attaches it — recording it here
              as well would count the same money twice.
            </Muted>
          </Small>
        </Prose>
        <Input
          control={form.control}
          name="amount"
          label={`What arrived, in ${invoice.currency}`}
          hint={`Outstanding is ${format.money(owed)}.`}
        />
        <Input
          control={form.control}
          name="note"
          label="What this was"
          hint="It stays on the record. “cheque 4417, banked 6 Aug”."
        />
        {isZero(amount) ? (
          <Notice need role="alert">
            A payment moves money. Put in what arrived.
          </Notice>
        ) : note.length < RECORD_NOTE_MIN ? (
          <Notice need role="alert">
            Say what this payment was. The note stays on the record.
          </Notice>
        ) : null}
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {record.isError ? (
          <Notice need role="alert">
            {messageOf(record.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* ---------------------------------------------------------------- write off */

/** `invoices_write_off!`'s own floor — `reason` is `.min(3)` on the published input. */
const WRITE_OFF_REASON_MIN = 3

const WriteOffFormSchema = z.object({
  amount: z.string(),
  reason: z.string(),
})

type WriteOffForm = z.infer<typeof WriteOffFormSchema>

/**
 * Money the studio has decided not to collect — `.docs/44-invoice-write-off.md`.
 *
 * The Record dialog's shape, field for field, because it is the same question asked
 * from the other side: an amount in a box that opens on what is outstanding, a
 * sentence that stays on the record, and a confirm button that reads the typed figure
 * back. The differences are the ones the service draws:
 *
 *   - **The reason is written to the client.** `InvoiceDetailSchema.write_offs`
 *     carries it on the client's own invoice route (decision 7), so the label says
 *     who can see it and the hint gives the kind of sentence that survives being read
 *     by them. `WRITE_OFF_REASON` is those words, shared with the generic form.
 *   - **More than is outstanding is refused here as well as there.** A write-off
 *     settles what is owed and never leaves the studio owing the client, and the
 *     figure to check against is on the invoice this dialog has just re-read — so the
 *     button is held rather than sent for a 422 the reader could have been told about.
 *   - **What it does not do is said before the click.** No payment row, so no cash
 *     figure moves; the invoice and its PDF stay as issued; nothing is mailed; and
 *     there is no reversal — a mistaken write-off is charged again on a new invoice
 *     (decisions 1, 6 and 9).
 *
 * What the service refuses that the row cannot see — a pending payment on a `sent`
 * invoice — comes back as its own sentence, under the fields, as every sibling here
 * shows its refusal.
 */
function WriteOffDialog({
  invoice,
  onClose,
}: {
  readonly invoice: InvoiceDetailView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const writeOff = useStudioAction('invoices_write_off')
  const review = reviewOf(writeOff.data)
  const owed = outstandingOf(invoice)
  const nothing = isZero(owed)

  /* The box opens on all of what is outstanding, which is the case that settles it.
     Less is a partial write-off and the confirm button reads whatever is typed. */
  const form = useZodForm<WriteOffForm, WriteOffForm>(WriteOffFormSchema, {
    defaultValues: { amount: nothing ? '' : String(toMajorUnits(owed)), reason: '' },
  })
  const typed = form.watch()
  const amount = amountOf(typed.amount, invoice.currency)
  const reason = typed.reason.trim()
  const noAmount = isZero(amount) || isNegative(amount)
  const tooMuch = compare(amount, owed) > 0
  const refused = nothing || noAmount || tooMuch || reason.length < WRITE_OFF_REASON_MIN

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Write off part of an invoice"
      confirmLabel={`Write off ${format.money(amount)} · ${invoice.ref}`}
      busy={writeOff.isPending || refused}
      onConfirm={() => {
        if (refused) return
        writeOff.mutate(
          { invoice_ref: invoice.ref, amount, reason },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`Money the studio has decided not to collect — an invoice issued for more than the fee that was agreed. `}
          {invoice.ref} has {format.money(owed)} outstanding. Writing all of it off settles the
          invoice; less leaves the rest still owed.
          {/*
            Decision 5: settling is `settle()` and nothing else, so a `final` invoice
            settled by a write-off releases the handover exactly as a payment would.
            The one consequence here that reaches past the invoice, so it is said.
          */}
          {invoice.kind === 'final'
            ? ' It is the project’s final invoice, so settling it releases the files it was holding back — the studio has decided not to be paid for them.'
            : null}
        </Prose>
        <Prose>
          <Small>
            <Muted>
              It is its own record and not a payment, so nothing counts as collected. The invoice
              and its PDF stay exactly as issued and nothing is emailed. It cannot be taken back
              here — a write-off made in error is charged again on a new invoice.
            </Muted>
          </Small>
        </Prose>
        <Input
          control={form.control}
          name="amount"
          label={`What to write off, in ${invoice.currency}`}
          hint={`Outstanding is ${format.money(owed)}. All of it settles the invoice.`}
        />
        <Textarea
          control={form.control}
          name="reason"
          label={WRITE_OFF_REASON.label}
          hint={WRITE_OFF_REASON.hint}
        />
        {nothing ? (
          <Notice need role="alert">
            Nothing is outstanding on this invoice, so there is nothing to write off.
          </Notice>
        ) : noAmount ? (
          <Notice need role="alert">
            A write-off is an amount the studio will not collect. Put in how much.
          </Notice>
        ) : tooMuch ? (
          <Notice need role="alert">
            That is more than the {format.money(owed)} outstanding. A write-off settles what is
            owed; it never leaves the studio owing the client.
          </Notice>
        ) : reason.length < WRITE_OFF_REASON_MIN ? (
          <Notice need role="alert">
            {WRITE_OFF_REASON.required}
          </Notice>
        ) : null}
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {writeOff.isError ? (
          <Notice need role="alert">
            {messageOf(writeOff.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* ------------------------------------------------------------------- refund */

const REFUND_NOTE_MIN = 3

const RefundFormSchema = z.object({
  payment: z.string(),
  amount: z.string(),
  note: z.string(),
})

type RefundForm = z.infer<typeof RefundFormSchema>

/**
 * Which payments can be given back.
 *
 * `charge.recordRefund` refuses a row that is not `succeeded` or `on_account`, and
 * refuses a `refund` row outright — "a refund is not a thing to refund" — so this is
 * that rule read off the fields `PaymentSchema` publishes rather than a guess. A
 * `refunded` row is one already given back in full and is refused for the same
 * reason; leaving it out is what keeps the picker from offering a choice the API
 * answers with a 409.
 */
export function refundable(payments: readonly PaymentView[]): readonly PaymentView[] {
  return payments.filter((one) => one.kind === 'payment' && one.status === 'succeeded')
}

function RefundDialog({
  invoice,
  onClose,
}: {
  readonly invoice: InvoiceDetailView
  readonly onClose: () => void
}) {
  const format = useFormat()
  const refund = useStudioAction('payments_refund')
  const review = reviewOf(refund.data)
  const choices = refundable(invoice.payments)
  const first = choices[0]

  const form = useZodForm<RefundForm, RefundForm>(RefundFormSchema, {
    defaultValues: {
      payment: first?.id ?? '',
      amount: first === undefined ? '' : String(toMajorUnits(first.amount)),
      note: '',
    },
  })
  const typed = form.watch()
  const chosen = choices.find((one) => one.id === typed.payment) ?? first
  const amount = amountOf(typed.amount, invoice.currency)
  const note = typed.note.trim()
  const refused = chosen === undefined || isZero(amount) || note.length < REFUND_NOTE_MIN

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Give a payment back"
      confirmLabel={
        chosen === undefined
          ? `Nothing to refund on ${invoice.ref}`
          : `Refund ${format.money(amount)} against ${invoice.ref}`
      }
      busy={refund.isPending || refused}
      onConfirm={() => {
        if (refused) return
        refund.mutate(
          { payment: chosen.id, amount, note },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        {chosen === undefined ? (
          <Notice need role="alert">
            Nothing has arrived against {invoice.ref} that can be given back. A payment has to have
            succeeded before it can be refunded, and one already refunded in full cannot be refunded
            again.
          </Notice>
        ) : (
          <>
            <Prose>
              {/*
                The distinction the reader is most likely to get wrong, said first.
                `ARCHITECTURE.md` §Deletion: an invoice is never edited, and money the
                studio decided not to charge is a `discount` line rather than money
                given back. Refunding to fix a wrong invoice sends real money out of
                the bank account.

                Which tool *is* the right one depends on the state (session 44): on an
                invoice still owed, what it should not have asked for is written off on
                it — the Write off button on the same row. A paid one cannot be written
                off, so its correction is still a discount line on the next invoice.
              */}
              This sends money back out. If the invoice was simply wrong, this is not the tool — an
              invoice is never edited,{' '}
              {writesOff(invoice.status)
                ? 'and what it should not have asked for is written off on it instead, with Write off on the same row.'
                : 'and a correction is a discount line on the next one.'}
            </Prose>
            <Prose>
              The refund is its own row and the original is untouched, so what{' '}
              <Ref value={invoice.ref} /> has settled stays the sum of the rows against it. A card
              or bank payment goes back through the gateway; a Wise transfer is recorded here and
              moved by a person at the bank.
            </Prose>
            <Select
              control={form.control}
              name="payment"
              label="Which payment"
              options={choices.map((one) => ({
                value: one.id,
                label: `${format.money(one.amount)} · ${one.source} · ${format.date(one.received_at, 'short', 'UTC')}`,
              }))}
            />
            <Input
              control={form.control}
              name="amount"
              label={`How much to give back, in ${invoice.currency}`}
              hint={`That payment was ${format.money(chosen.amount)}. Less is a partial refund.`}
            />
            <Input
              control={form.control}
              name="note"
              label="Why"
              hint="It stays on the record. “duplicate transfer, returned 14 Aug”."
            />
            {isZero(amount) ? (
              <Notice need role="alert">
                A refund gives money back. Put in how much.
              </Notice>
            ) : note.length < REFUND_NOTE_MIN ? (
              <Notice need role="alert">
                Say why it is being given back. The note stays on the record.
              </Notice>
            ) : null}
          </>
        )}
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {refund.isError ? (
          <Notice need role="alert">
            {messageOf(refund.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* -------------------------------------------------------------- the invoice */

/** What is on it, so Issue is confirmed against the lines and not just a total. */
function InvoiceLines({ invoice }: { readonly invoice: InvoiceDetailView }) {
  return (
    <Stack gap="tight">
      {invoice.lines.map((line) => (
        <Small key={line.seq}>
          <Muted>{line.description}</Muted> <Num value={line.amount} />
        </Small>
      ))}
      <Small>
        <B>
          Total <Num value={invoice.total} />
        </B>
      </Small>
    </Stack>
  )
}
